import { useEffect, useState } from "react";
import { Bell, Download, Smartphone, X } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { getCookieConsent } from "./CookieConsent";

const STORAGE_KEY = "smartech_notifications_prompt_v1";

// Build API base URL the same way other modules do
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getApiUrl = () => {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  if (hostname === "smartechsystempro.com" || hostname === "www.smartechsystempro.com")
    return `${protocol}//${hostname}/api`;
  if (hostname.includes("emergent.host") || hostname.includes("emergentagent.com"))
    return `${protocol}//${hostname}/api`;
  return BACKEND_URL ? `${BACKEND_URL}/api` : "/api";
};
const API = getApiUrl();

const getAuthToken = () =>
  localStorage.getItem("token") || localStorage.getItem("client_token") || "";

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) outputArray[i] = rawData.charCodeAt(i);
  return outputArray;
};

// Subscribe the browser to Web Push and send the subscription to backend.
// Safe to call multiple times — backend upserts by endpoint.
export const subscribePushIfPossible = async () => {
  try {
    if (!("serviceWorker" in navigator) || !("PushManager" in window)) return false;
    const token = getAuthToken();
    if (!token) return false; // user must be authenticated to subscribe
    if (Notification.permission !== "granted") return false;

    const registration = await navigator.serviceWorker.ready;

    // Fetch public VAPID key from backend
    const keyResp = await axios.get(`${API}/push/public-key`);
    const publicKey = keyResp.data?.public_key;
    if (!publicKey) return false;

    let sub = await registration.pushManager.getSubscription();
    if (!sub) {
      sub = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }

    await axios.post(
      `${API}/push/subscribe`,
      { subscription: sub.toJSON ? sub.toJSON() : sub },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return true;
  } catch (e) {
    // Non-fatal; device may not support or user in private mode
    return false;
  }
};

// Decide if we should show the banner
const shouldShow = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "activated" || stored === "later") return false;
    const cookie = getCookieConsent();
    if (!cookie) return false;
    const hasNotificationAPI = typeof window !== "undefined" && "Notification" in window;
    const notifPending = hasNotificationAPI && Notification.permission === "default";
    const isStandalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    const pwaInstalled = localStorage.getItem("smartech_pwa_installed") === "1";
    const installAvailable = !!window.__smartechDeferredInstall;
    return notifPending || (!isStandalone && !pwaInstalled && installAvailable);
  } catch {
    return false;
  }
};

export const NotificationsPrompt = () => {
  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [installAvailable, setInstallAvailable] = useState(false);

  // Silent re-subscribe on every mount (if permission already granted)
  useEffect(() => {
    subscribePushIfPossible();
  }, []);

  useEffect(() => {
    let attempts = 0;
    const interval = setInterval(() => {
      attempts += 1;
      setInstallAvailable(!!window.__smartechDeferredInstall);
      if (shouldShow()) {
        setVisible(true);
        clearInterval(interval);
      }
      if (attempts >= 15) clearInterval(interval);
    }, 1000);

    const onAvail = () => {
      setInstallAvailable(true);
      if (shouldShow()) setVisible(true);
    };
    window.addEventListener("smartech-install-available", onAvail);

    const onStorage = () => { if (shouldShow()) setVisible(true); };
    window.addEventListener("storage", onStorage);

    return () => {
      clearInterval(interval);
      window.removeEventListener("smartech-install-available", onAvail);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const persist = (v) => { try { localStorage.setItem(STORAGE_KEY, v); } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[NotifPrompt] localStorage failed:", e?.message); } };

  const activate = async () => {
    setBusy(true);
    let anySuccess = false;

    // 1) Ask for notification permission
    if ("Notification" in window) {
      try {
        const perm = await Notification.requestPermission();
        if (perm === "granted") {
          anySuccess = true;
          // Subscribe to push so the bubble receives real push notifications
          const okSub = await subscribePushIfPossible();
          if (okSub) {
            toast.success("Notifications activées !");
          } else {
            toast.info("Notifications activées. Connectez-vous pour recevoir les alertes sur votre compte.");
          }
          // Trigger a local notification to confirm
          try {
            new Notification("SMARTECH PRO", {
              body: "Notifications activées ! Vous recevrez les mises à jour importantes.",
              icon: "https://customer-assets.emergentagent.com/job_affiliate-manage-1/artifacts/24go47ow_20251231_131530.png",
            });
          } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[NotifPrompt] local notif failed:", e?.message); }
        } else if (perm === "denied") {
          toast.error("Les notifications ont été refusées. Activez-les depuis les réglages du navigateur.");
        }
      } catch (e) { if (process.env.NODE_ENV === "development") console.debug("[NotifPrompt] requestPermission failed:", e?.message); }
    }

    // 2) Trigger PWA install if available
    const deferred = window.__smartechDeferredInstall;
    if (deferred && typeof deferred.prompt === "function") {
      try {
        deferred.prompt();
        const choice = await deferred.userChoice;
        if (choice && choice.outcome === "accepted") {
          anySuccess = true;
          toast.success("Installation en cours !");
        }
        window.__smartechDeferredInstall = null;
      } catch { /* ignore */ }
    } else if (!anySuccess) {
      const ua = navigator.userAgent || "";
      const isIOS = /iPhone|iPad|iPod/i.test(ua);
      if (isIOS) {
        toast.info("iOS : appuyez sur Partager ↗ puis 'Sur l'écran d'accueil' pour installer SMARTECH.", { duration: 6000 });
      }
    }

    persist("activated");
    setBusy(false);
    setVisible(false);
  };

  const later = () => { persist("later"); setVisible(false); };

  if (!visible) return null;

  return (
    <div className="notif-prompt" role="dialog" aria-label="Notifications & accès rapide" data-testid="notif-prompt-banner">
      <div className="notif-prompt-inner">
        <button
          className="notif-prompt-close"
          onClick={later}
          aria-label="Fermer"
          data-testid="notif-prompt-close"
        >
          <X size={16} />
        </button>
        <div className="notif-prompt-icons">
          <div className="notif-prompt-icon"><Bell size={18} /></div>
          <div className="notif-prompt-icon"><Smartphone size={18} /></div>
        </div>
        <div className="notif-prompt-text">
          <div className="notif-prompt-title">🔔 Notifications &amp; accès rapide</div>
          <p className="notif-prompt-body">
            Activez les notifications et ajoutez la plateforme en bulle sur votre écran
            d'accueil pour recevoir en temps réel :
            <br />
            <b>nouvelles commandes, transactions en attente, paiements validés et activités de votre compte.</b>
            <br />
            📲 Gérez vos opérations plus rapidement, que vous soyez <b>Client, Affilié ou Admin</b>.
            <br />
            👉 Souhaitez-vous activer les notifications et installer l'accès rapide ?
          </p>
        </div>
        <div className="notif-prompt-actions">
          <button
            className="notif-btn notif-btn-primary"
            onClick={activate}
            disabled={busy}
            data-testid="notif-prompt-activate"
          >
            <Download size={14} /> {busy ? "Activation..." : "Activer maintenant"}
          </button>
          <button
            className="notif-btn notif-btn-later"
            onClick={later}
            data-testid="notif-prompt-later"
          >
            Plus tard
          </button>
          {!installAvailable && (
            <div className="notif-install-hint">
              💡 iOS : Partager ↗ → "Sur l'écran d'accueil"
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsPrompt;
